import { ColumnValue } from '@Components/ColumnValue';
import type { ApiInvoice, ApiInvoiceEmailDelivery, EnumInvoiceType } from '@Model';
import {
    emailStateOptions,
    EnumProcessState,
    getAggregatedEmailStatus,
    getBadgeVariantByProcessState,
    getEnumEmailStateLabel,
    getEnumInvoiceTypeLabel,
    getEnumProcessStateLabel,
    invoiceTypes,
    processStateOptions,
} from '@Model';
import {
    Badge,
    type Column,
    isNullOrEmpty,
    type ITableWithStoredSettingsProps,
    renderDateTime,
    type Undefined,
} from '@synqo/frontend-component-library';
import { isDateBeforeToday } from '@utils/columnModificators';

export const initialInvoiceSorting: Readonly<ITableWithStoredSettingsProps<ApiInvoice>['initialSorting']> = {
    columnName: 'date',
    direction: 'desc',
};

export const baseInvoiceColumns: readonly Readonly<Column<ApiInvoice>>[] = [
    { name: 'number', label: 'Nummer', size: 120 },
    {
        name: 'type',
        label: 'Rechungsart',
        size: 130,
        filterVariant: 'select',
        filterOptions: invoiceTypes,
        renderCell: ({ cellData }) => getEnumInvoiceTypeLabel(cellData as EnumInvoiceType),
    },
    { name: 'customer.display_string', label: 'Kunde', size: 200 },
    {
        name: 'process_state',
        label: 'Status',
        size: 120,
        filterVariant: 'select',
        filterOptions: processStateOptions,
        renderCell: ({ cellData }) => (
            <Badge variant={getBadgeVariantByProcessState(cellData as EnumProcessState)}>
                {getEnumProcessStateLabel(cellData as EnumProcessState)}
            </Badge>
        ),
    },
    {
        name: 'emails.email_status',
        label: 'Letze E-Mail',
        size: 130,
        filterVariant: 'select',
        filterOptions: emailStateOptions,
        renderCell: renderLatestEmailOfInvoice,
    },
    { name: 'total_gross_price', label: 'Brutto in €', type: 'currency', size: 120 },
    { name: 'date', label: 'Datum', type: 'date', filterVariant: 'day-range', size: 110 },
    {
        name: 'due_at',
        label: 'Fällig am',
        type: 'date',
        filterVariant: 'day-range',
        size: 110,
        renderCell: ({ cellData, rowData }) => {
            if (isDateBeforeToday(cellData) && isNullOrEmpty(rowData['payment_completed_at'])) {
                return (
                    <ColumnValue variant="danger" noIcon>
                        {renderDateTime(cellData as string, 'date')}
                    </ColumnValue>
                );
            }

            return renderDateTime(cellData as Undefined<string>, 'date');
        },
    },
];

export const openInvoiceColumns: Column<ApiInvoice>[] = [
    ...baseInvoiceColumns,
    {
        name: 'admonished_at',
        label: 'Gemahnt am',
        type: 'date',
        renderCell: ({ cellData }) => (
            <ColumnValue variant={(cellData as Undefined<string>)?.length ? 'danger' : 'default'}>
                {renderDateTime(cellData as string, 'date')}
            </ColumnValue>
        ),
    },
];

export const exportInvoiceColumns: Column<ApiInvoice>[] = [...baseInvoiceColumns];

export const invoiceColumns: Column<ApiInvoice>[] = [
    ...openInvoiceColumns,
    {
        name: 'payment_completed_at',
        label: 'Bezahlt am',
        type: 'date',
        renderCell: ({ cellData }) => (
            <ColumnValue variant={(cellData as Undefined<string>)?.length ? 'success' : 'default'}>
                {renderDateTime(cellData as string, 'date')}
            </ColumnValue>
        ),
    },
];

function renderLatestEmailOfInvoice({ rowData }: { rowData: ApiInvoice }): string {
    return renderLatestEmail(rowData.emails);
}

export function renderLatestEmail(emails: ApiInvoiceEmailDelivery[]): string {
    if (emails.length === 0) {
        return '';
    }

    const sortedEmails = [...emails].sort((a, b) => b.sent_date.localeCompare(a.sent_date));

    const latestEmail = sortedEmails[0];
    const latestGroupId = latestEmail.email_group_id || latestEmail.id;

    const groupEmails = emails.filter((email) => email.email_group_id === latestGroupId || email.id === latestGroupId);

    const aggregatedStatus = getAggregatedEmailStatus(groupEmails);
    return getEnumEmailStateLabel(aggregatedStatus);
}
